"use client";

import { useCallback, useRef, useState } from "react";

import { AssetImage } from "@/components/optilase/asset-image";
import { CarouselDots } from "@/components/optilase/carousel-dots";
import { activeSlide } from "@/components/optilase/carousel-page";
import { TextLink } from "@/components/optilase/text-link";
import { headingOn, type Surface } from "@/components/optilase/surface";
import { useScrollNeedsFocus } from "@/lib/use-scrollable";
import { cn } from "@/lib/utils";
import { headingLines } from "@/lib/heading-lines";

export type ProcedureCard = {
  image: string;
  imageAlt?: string;
  imageFocus?: string;
  title: string;
  body: React.ReactNode;
  linkLabel?: string;
  href: string;
};

export type ProcedureCardsViewProps = {
  heading: string;
  intro?: string;
  cards: ProcedureCard[];
  variant?: "procedure" | "person";
  cardSurface?: Surface | null;
  surface?: Surface | null;
};

export default function ProcedureCardsView({
  heading,
  intro,
  cards,
  variant = "procedure",
  cardSurface,
  surface,
}: ProcedureCardsViewProps) {
  const person = variant === "person";
  // Two cards have room to fill the tablet row instead of scrolling.
  const twoUp = !person && cards.length === 2;
  const cols = Math.min(cards.length, person ? 4 : 3);
  const gridCols =
    cols >= 4
      ? "lg:grid-cols-4"
      : cols === 3
        ? "lg:grid-cols-3"
        : cols === 2
          ? "lg:grid-cols-2"
          : "lg:grid-cols-1 lg:max-w-200";
  const scroller = useRef<HTMLUListElement>(null);
  const isScrollable = useScrollNeedsFocus(scroller);
  const [page, setPage] = useState(0);

  const sync = useCallback(() => {
    const el = scroller.current;
    if (el) setPage(activeSlide(el));
  }, []);

  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn(
        "px-5 py-10 md:px-10 md:py-10 lg:px-20 lg:py-20",
        surface ? "" : person ? "bg-white" : "bg-teal-02",
      )}
    >
      <div className="mx-auto max-w-7xl">
        <h2 className={cn("text-section max-w-[1048px]", headingOn(surface, "text-teal-08"))}>
          {headingLines(heading)}
        </h2>
        {intro ? (
          <p className="text-grey-07 mt-5 max-w-[809px] text-base leading-relaxed tracking-[-0.01em] lg:text-xl">
            {intro}
          </p>
        ) : null}

        <ul
          ref={scroller}
          onScroll={sync}
          tabIndex={isScrollable ? 0 : -1}
          aria-label={isScrollable ? heading : undefined}
          className={cn(
            "-mx-5 mt-8 flex snap-x snap-mandatory scroll-pl-5 [scrollbar-width:none] gap-4 overflow-x-auto px-5 pb-2 md:-mx-10 md:scroll-pl-10 md:gap-6 md:px-10 lg:mx-0 lg:mt-10 lg:grid lg:overflow-visible lg:px-0 lg:pb-0 [&::-webkit-scrollbar]:hidden",
            gridCols,
            twoUp && "md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 md:pb-0",
            person && "lg:gap-[18px]",
          )}
        >
          {cards.map((card) => (
            <li
              key={card.title}
              className={cn(
                "relative flex shrink-0 snap-start flex-col lg:w-auto",
                person ? "w-[260px] md:w-[280px]" : "w-[302px] md:w-[292px]",
                twoUp && "md:w-auto",
              )}
            >
              <div
                className={cn(
                  "relative w-full overflow-hidden",
                  person ? "rounded-t-[12px]" : "rounded-t-2xl",
                  person ? "h-[280px] md:h-[300px] lg:h-[340px]" : "h-65 lg:h-[323px]",
                )}
              >
                <AssetImage
                  src={card.image}
                  focus={card.imageFocus}
                  alt={card.imageAlt ?? ""}
                  fill
                  sizes="(min-width: 1115px) 628px, 302px"
                  className="object-cover object-top"
                />
              </div>

              <div
                style={
                  person && cardSurface
                    ? { backgroundColor: cardSurface.backgroundColor }
                    : undefined
                }
                className={cn(
                  "relative flex flex-1 flex-col",
                  person ? "rounded-b-[12px]" : "rounded-b-2xl",
                  person
                    ? cn("gap-2 p-7", cardSurface ? "" : "bg-teal-01")
                    : "-mt-15 gap-4 bg-white py-6 pr-6 pl-6 lg:pr-14",
                )}
              >
                <h3
                  className={cn(person ? "text-h5 text-grey-09 font-bold" : "text-h5 text-grey-08")}
                >
                  {card.title}
                </h3>
                {card.body ? (
                  <p
                    className={cn(
                      "leading-relaxed tracking-[-0.01em]",
                      person ? "text-grey-06 text-base" : "text-grey-06 text-base",
                    )}
                  >
                    {card.body}
                  </p>
                ) : null}
                <TextLink
                  href={card.href}
                  className={cn(
                    "mt-auto pt-4 whitespace-normal",
                    person ? "text-teal-08" : "text-teal-07",
                  )}
                >
                  {card.linkLabel ?? "View treatment"}
                </TextLink>
              </div>
            </li>
          ))}
        </ul>

        <CarouselDots
          count={cards.length}
          active={page}
          label="procedure"
          onSelect={(i) => {
            const card = scroller.current?.children[i] as HTMLElement | undefined;
            card?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
          }}
          className={twoUp ? "md:hidden" : "lg:hidden"}
        />
      </div>
    </section>
  );
}
